// src/pages/App.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink } from 'react-router-dom';
import { MdSettings, MdNotifications } from 'react-icons/md';
import '../App.css';
import SchedulePage from '../schedule/SchedulePage';
import AdminPage from './AdminPage';
import PaymentsPage from '../pages/PaymentsPage';
import { usePlannerData } from './usePlannerData';
import ThemeToggle from '../theme/ThemeToggle';
import LoginForm from '../components/LoginForm';
import NotificationQueue from '../components/NotificationQueue';
import NotificationQueueEditor from '../components/NotificationQueueEditor';
import NotificationsPanel from '../components/NotificationsPanel';
import NotificationPermissionPrompt from '../components/NotificationPermissionPrompt';
import { usePushSubscription } from '../hooks/usePushSubscription';
import { useNotificationQueue } from '../hooks/useNotificationQueue';
import { getApiBase } from '../utils/getApiBase';
import { resolveEmployeeRecipient, shouldEnqueueStaffNotification } from '../utils/notificationRecipient';
import { 
    loadPaymentRates, 
    savePaymentRates, 
    loadEventStaffRoles, 
    saveEventStaffRoles,
    updateEventStaffRole,
    loadEventAmounts,
    saveEventAmounts,
    updateEventAmount,
    autoAssignRolesFromStaff,
    loadEventStaffPayments,
    saveEventStaffPayments,
    updateEventStaffPayment,
    loadDefaultFunctionRates,
    saveDefaultFunctionRates,
    loadTaxRate,
    saveTaxRate
} from '../utils/paymentStorage';

export default function App() {
    // 🔹 Stan logowania - nowy system
    const [user, setUser] = useState(null);
    const [userType, setUserType] = useState(null);

    // 🔹 Stan menu
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // 🔹 Kolejka powiadomień
    const notificationQueue = useNotificationQueue();
    const [isNotificationEditorOpen, setIsNotificationEditorOpen] = useState(false);
    const [isNotificationsPanelOpen, setIsNotificationsPanelOpen] = useState(false);
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
    const [pushToast, setPushToast] = useState(null); // Toast w aplikacji gdy push przychodzi w foreground (PWA)
    const loadNotificationsRef = useRef(null); // Ref do funkcji loadNotifications z NotificationsPanel
    
    // Ustaw badge na ikonie aplikacji gdy liczba powiadomień się zmienia
    useEffect(() => {
        if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
            const totalCount = unreadNotificationsCount + notificationQueue.queueCount;
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            
            if (totalCount > 0) {
                navigator.setAppBadge(totalCount).then(() => {
                    // Zawsze loguj sukces (dla debugowania mobile)
                    console.log(`[App] ✅ Badge ustawiony na ${totalCount}`);
                    if (isMobile) {
                        console.log('[App] 📱 Mobile: Badge ustawiony pomyślnie');
                    }
                }).catch(err => {
                    // Zawsze loguj błędy (dla debugowania mobile)
                    console.warn('[App] ⚠️ Nie udało się ustawić badge:', err);
                    if (isMobile) {
                        console.error('[App] 📱 Mobile: Błąd ustawiania badge:', err.message);
                        console.info('[App] 📱 Mobile: Sprawdź czy aplikacja jest zainstalowana jako PWA');
                    }
                });
            } else {
                navigator.clearAppBadge().then(() => {
                    if (process.env.NODE_ENV === 'development') {
                        console.log('[App] ✅ Badge wyczyszczony');
                    }
                }).catch(() => {});
            }
        } else {
            // Sprawdź czy jesteśmy na mobile
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (isMobile) {
                console.warn('[App] 📱 Mobile: Badge API nie jest wspierane w tej przeglądarce');
            }
        }
    }, [unreadNotificationsCount, notificationQueue.queueCount]);
    
    // Funkcja do aktualizacji powiadomień (wywoływana z zewnątrz)
    const refreshNotifications = useCallback(() => {
        if (loadNotificationsRef.current) {
            loadNotificationsRef.current();
        }
    }, []);

    const [showNotificationPermissionPrompt, setShowNotificationPermissionPrompt] = useState(false);

    // 🔹 Stałe
    const months = [
        'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
        'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
    ];

    function stringToColor(str) {
        if (!str || typeof str !== 'string') str = 'Brak nazwy';
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const hue = Math.abs(hash) % 360;
        const sat = 55;
        const light = 70;

        const h = hue / 360, s = sat / 100, l = light / 100;
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
        const g = Math.round(hue2rgb(p, q, h) * 255);
        const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
        return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
    }

    // 🔹 Dzisiejsza data ISO jako STRING (dla hooka)
    const todayIso = useMemo(() => {
        const now = new Date();
        const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
        return local.toISOString().slice(0, 10);
    }, []);

    // 🔹 Dane harmonogramu (loading przekazujemy do SchedulePage)
    const {
        eventsByDate,
        employees,
        calendarList,
        selectedCalendar,
        theatreCalendar,
        setEmployees,
        updateEventStaff,
        updateEventColor,
        changeCalendarAndReload,
        loading,
        isSyncing,
        updateEmployees,
        updateEmployeesAndEvents,
        unavailableByDate,
        toggleUnavailable,
        addUnavailableRange,
        updateNeededForEvent,
        copyStaffToAllDates,
        changeTheatreCalendar,
        reloadFromGoogle,
        firestoreQuotaExceeded,
    } = usePlannerData(todayIso, stringToColor, refreshNotifications);

    // 🔹 Wrapper dla updateEventStaff - agreguje zmiany do kolejki powiadomień
    const syncRolesFromStaff = useCallback((date, eventKey, staffList) => {
        setEventStaffRoles((prevRoles) => {
            const next = prevRoles ? JSON.parse(JSON.stringify(prevRoles)) : {};
            const normalizedEvent = String(eventKey || '').trim();
            const names = (staffList || [])
                .map((staff) => (typeof staff === 'string' ? staff : staff?.name))
                .filter(Boolean);

            if (!normalizedEvent || !date) {
                return prevRoles;
            }

            if (names.length === 0) {
                if (next[date]) {
                    delete next[date][normalizedEvent];
                    if (Object.keys(next[date]).length === 0) {
                        delete next[date];
                    }
                }
                return next;
            }

            if (!next[date]) next[date] = {};
            if (!next[date][normalizedEvent]) next[date][normalizedEvent] = {};

            const roleForIndex = (idx) => (idx === 0 ? 'stage_manager' : 'inspicjent');
            const assigned = new Set();
            names.forEach((name, idx) => {
                assigned.add(name);
                next[date][normalizedEvent][name] = roleForIndex(idx);
            });

            Object.keys(next[date][normalizedEvent]).forEach((name) => {
                if (!assigned.has(name)) {
                    delete next[date][normalizedEvent][name];
                }
            });

            return next;
        });
    }, []);

    const updateEventStaffWithNotifications = useCallback((date, eventName, newStaff, calendarId, save = true) => {
        // Tylko dla administratorów agreguj powiadomienia
        if ((userType === 'admin' || userType === 'superAdmin') && save) {
            // Pobierz aktualny event, aby porównać staff
            const eventsForDate = eventsByDate[date] || [];
            const eventObj = typeof eventName === 'object' ? eventName : null;
            const eventNameStr = eventObj ? (eventObj.event || eventObj.name || '') : String(eventName || '');
            const gId = eventObj?.gId;
            
            // Znajdź event
            let currentEvent = null;
            if (gId && calendarId) {
                currentEvent = eventsForDate.find(ev => ev.gId === gId && ev.calendarId === calendarId);
            }
            if (!currentEvent && eventNameStr) {
                const normName = (s) => (s || '').trim().toLowerCase();
                currentEvent = eventsForDate.find(ev => 
                    normName(ev.event) === normName(eventNameStr) && 
                    (calendarId ? ev.calendarId === calendarId : true)
                );
            }
            
            if (currentEvent) {
                const oldStaff = currentEvent.staff || [];
                const newStaffArray = Array.isArray(newStaff) ? newStaff : [];
                
                // Funkcja pomocnicza do normalizacji nazwy pracownika
                const getStaffName = (staff) => {
                    if (typeof staff === 'string') {
                        // Usuń (?) z końca dla porównania
                        return staff.replace(/\s+\(\?\)$/, '');
                    }
                    return staff?.name || '';
                };
                
                // Funkcja pomocnicza do sprawdzania czy osoba ma pytajnik (tentative)
                const isTentative = (staff) => {
                    if (typeof staff === 'object') {
                        return staff.tentative === true;
                    }
                    if (typeof staff === 'string') {
                        return staff.endsWith(' (?)');
                    }
                    return false;
                };
                
                const eventKey = currentEvent.event || eventNameStr;
                const currentUserId = user?.id || user?.login || user?.name || 'unknown';
                const currentUserName = user?.name || user?.login || user?.id || 'Administrator';

                const findEmployeeId = (staffName) => {
                    const recipient = resolveEmployeeRecipient(staffName, null, employees);
                    return recipient.mapped ? recipient.staffId : null;
                };

                const recipientFields = (staffName, staffId) => {
                    const recipient = resolveEmployeeRecipient(staffName, staffId, employees);
                    return {
                        staffId: recipient.staffId,
                        userId: recipient.userId,
                        lookupKeys: recipient.lookupKeys,
                        mappingError: recipient.mapped ? undefined : `Nie znaleziono konta dla „${staffName}”`,
                    };
                };

                // Funkcja sprawdzająca czy pracownik to aktualny użytkownik
                const isCurrentUser = (staffName, staffId) => {
                    if (!staffName && !staffId) return false;
                    
                    // Sprawdź po ID
                    if (staffId && currentUserId && currentUserId !== 'unknown') {
                        const staffIdLower = String(staffId).trim().toLowerCase();
                        const currentUserIdLower = String(currentUserId).trim().toLowerCase();
                        if (staffIdLower === currentUserIdLower) return true;
                    }
                    
                    // Sprawdź po nazwie
                    if (staffName) {
                        const staffNameLower = String(staffName).trim().toLowerCase();
                        const currentUserNameLower = String(currentUserName).trim().toLowerCase();
                        const currentUserIdLower = String(currentUserId).trim().toLowerCase();
                        
                        // Porównaj z nazwą użytkownika
                        if (staffNameLower === currentUserNameLower) return true;
                        
                        // Porównaj z ID użytkownika (jeśli nazwa pracownika to ID)
                        if (staffNameLower === currentUserIdLower && currentUserIdLower !== 'unknown') return true;
                        
                        // Sprawdź czy w liście pracowników jest dopasowanie
                        const staffIdFromName = findEmployeeId(staffName);
                        if (staffIdFromName && currentUserId && currentUserId !== 'unknown') {
                            const staffIdLower = String(staffIdFromName).trim().toLowerCase();
                            const currentUserIdLower = String(currentUserId).trim().toLowerCase();
                            if (staffIdLower === currentUserIdLower) return true;
                        }
                    }
                    
                    return false;
                };
                
                // Utwórz mapę starych pracowników (dla porównania)
                const oldStaffMap = new Map();
                oldStaff.forEach(staff => {
                    const staffName = getStaffName(staff);
                    if (staffName) {
                        oldStaffMap.set(staffName, {
                            tentative: isTentative(staff),
                        });
                    }
                });
                
                // Utwórz mapę ostatecznego statusu każdego pracownika
                const finalStatus = new Map();
                newStaffArray.forEach(staff => {
                    const staffName = getStaffName(staff);
                    if (staffName) {
                        finalStatus.set(staffName, {
                            exists: true,
                            tentative: isTentative(staff),
                        });
                    }
                });
                
                // Sprawdź pracowników, którzy zostali usunięci
                oldStaffMap.forEach((oldStatus, staffName) => {
                    if (!finalStatus.has(staffName)) {
                        if (!shouldEnqueueStaffNotification(staffName, employees)) {
                            notificationQueue.removeFromQueueByKey(eventKey, date, staffName);
                            return;
                        }
                        // Pracownik został usunięty
                        const staffId = findEmployeeId(staffName);
                        
                        // Nie wysyłaj powiadomienia do samego siebie
                        if (isCurrentUser(staffName, staffId)) {
                            return;
                        }
                        
                        // Sprawdź czy powiadomienie o dodaniu zostało już wysłane
                        const lastSentState = notificationQueue.getLastSentState(eventKey, date, staffName);
                        
                        notificationQueue.removeFromQueueByKey(eventKey, date, staffName);

                        notificationQueue.addToQueue({
                            type: 'remove_staff',
                            eventName: eventKey,
                            date,
                            staffName,
                            ...recipientFields(staffName, staffId),
                            eventId: currentEvent.gId,
                            calendarId: currentEvent.calendarId || calendarId,
                            requestedBy: currentUserId,
                            requestedByName: currentUserName,
                        });
                        
                        // Usuń ostatni wysłany stan (pracownik już nie jest w wydarzeniu)
                        notificationQueue.clearLastSentState(eventKey, date, staffName);
                    }
                });
                
                // Sprawdź ostateczny status każdego pracownika i zaktualizuj powiadomienia
                finalStatus.forEach((status, staffName) => {
                    if (!shouldEnqueueStaffNotification(staffName, employees)) {
                        notificationQueue.removeFromQueueByKey(eventKey, date, staffName);
                        return;
                    }
                    const staffId = findEmployeeId(staffName);
                    
                    // Nie wysyłaj powiadomienia do samego siebie
                    if (isCurrentUser(staffName, staffId)) {
                        return;
                    }
                    
                    const oldStatus = oldStaffMap.get(staffName);
                    const lastSentState = notificationQueue.getLastSentState(eventKey, date, staffName);
                    
                    // Sprawdź czy status się zmienił (nowy pracownik lub zmiana tentative)
                    const isNew = !oldStatus;
                    const tentativeChanged = oldStatus && oldStatus.tentative !== status.tentative;
                    
                    // Jeśli powiadomienie zostało już wysłane, porównaj z ostatnim wysłanym stanem
                    if (lastSentState && lastSentState.sent) {
                        const lastSentType = lastSentState.type;
                        const currentType = status.tentative ? 'propose_staff' : 'add_staff';
                        
                        // Jeśli typ się nie zmienił i pracownik już był, nie dodawaj powiadomienia
                        if (lastSentType === currentType && !isNew && !tentativeChanged) {
                            return;
                        }
                        
                        // Jeśli typ się zmienił (np. z add_staff na propose_staff), dodaj nowe powiadomienie
                        if (lastSentType !== currentType) {
                            notificationQueue.removeFromQueueByKey(eventKey, date, staffName);
                            if (status.tentative) {
                            notificationQueue.addToQueue({
                                type: 'propose_staff',
                                eventName: eventKey,
                                date,
                                staffName,
                                ...recipientFields(staffName, staffId),
                                eventId: currentEvent.gId,
                                calendarId: currentEvent.calendarId || calendarId,
                                requestedBy: currentUserId,
                                requestedByName: currentUserName,
                            });
                            } else {
                                notificationQueue.addToQueue({
                                    type: 'add_staff',
                                    eventName: eventKey,
                                    date,
                                    staffName,
                                    ...recipientFields(staffName, staffId),
                                    eventId: currentEvent.gId,
                                    calendarId: currentEvent.calendarId || calendarId,
                                    requestedBy: currentUserId,
                                    requestedByName: currentUserName,
                                });
                            }
                            return;
                        }
                    }
                    
                    // Jeśli pracownik już był i status się nie zmienił, nie dodawaj powiadomienia
                    if (!isNew && !tentativeChanged) {
                        return;
                    }
                    
                    // Usuń wszystkie stare powiadomienia dla tego pracownika
                    notificationQueue.removeFromQueueByKey(eventKey, date, staffName);
                    
                    // Dodaj powiadomienie dla ostatecznego statusu
                    if (status.tentative) {
                        // Ostateczny status: propozycja (z pytajnikiem)
                    notificationQueue.addToQueue({
                        type: 'propose_staff',
                        eventName: eventKey,
                        date,
                        staffName,
                        ...recipientFields(staffName, staffId),
                        eventId: currentEvent.gId,
                        calendarId: currentEvent.calendarId || calendarId,
                        requestedBy: currentUserId,
                        requestedByName: currentUserName,
                    });
                    } else {
                        // Ostateczny status: dodany (bez pytajnika)
                        notificationQueue.addToQueue({
                            type: 'add_staff',
                            eventName: eventKey,
                            date,
                            staffName,
                            ...recipientFields(staffName, staffId),
                            eventId: currentEvent.gId,
                            calendarId: currentEvent.calendarId || calendarId,
                            requestedBy: currentUserId,
                            requestedByName: currentUserName,
                        });
                    }
                });
            }
        }
        
        // Wywołaj oryginalną funkcję
        const result = updateEventStaff(date, eventName, newStaff, calendarId, save);

        // Zsynchronizuj role natychmiast, aby eventStaffRoles nie nadpisywało zmian
        const eventObj = typeof eventName === 'object' ? eventName : null;
        const eventKey = eventObj?.event || eventObj?.name || String(eventName || '');
        syncRolesFromStaff(date, eventKey, Array.isArray(newStaff) ? newStaff : []);

        return result;
    }, [updateEventStaff, eventsByDate, userType, notificationQueue, syncRolesFromStaff, employees, user]);

    const [activeMonthKey, setActiveMonthKey] = useState(() => {
        const today = new Date();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        return `${today.getFullYear()}-${mm}`;
    });

    // Stan dla kwot i stanowisk (początkowo z localStorage, potem zaktualizowane z DB)
    const [paymentRates, setPaymentRates] = useState(() => loadPaymentRates());
    const [eventStaffRoles, setEventStaffRoles] = useState(() => loadEventStaffRoles());
    const [eventAmounts, setEventAmounts] = useState(() => loadEventAmounts());
    const [eventStaffPayments, setEventStaffPayments] = useState(() => loadEventStaffPayments());
    const [defaultFunctionRates, setDefaultFunctionRates] = useState(() => loadDefaultFunctionRates());
    const [taxRate, setTaxRate] = useState(() => loadTaxRate());
    const [paymentDataLoaded, setPaymentDataLoaded] = useState(false);
    const paymentSnapshotRef = useRef(null);
    // Pierwszy przebieg po hydracji z bazy nie może z powrotem zapisać tych samych stawek.
    // Taki zapis wysyłał cichy push i otwarte karty znowu wołały loadData.
    const skipPaymentPersistRef = useRef(true);

    // Zapisywanie danych płatności do bazy - tylko gdy dane są już załadowane z bazy (żeby nie nadpisać podczas ładowania)
    useEffect(() => {
        if (!paymentDataLoaded || skipPaymentPersistRef.current) return;
        savePaymentRates(paymentRates);
    }, [paymentRates, paymentDataLoaded]);

    useEffect(() => {
        if (!paymentDataLoaded || skipPaymentPersistRef.current) return;
        saveEventAmounts(eventAmounts);
    }, [eventAmounts, paymentDataLoaded]);

    useEffect(() => {
        if (!paymentDataLoaded || skipPaymentPersistRef.current) return;
        saveEventStaffPayments(eventStaffPayments);
    }, [eventStaffPayments, paymentDataLoaded]);

    useEffect(() => {
        if (!paymentDataLoaded || skipPaymentPersistRef.current) return;
        saveEventStaffRoles(eventStaffRoles);
    }, [eventStaffRoles, paymentDataLoaded]);

    useEffect(() => {
        if (!paymentDataLoaded || skipPaymentPersistRef.current) return;
        saveDefaultFunctionRates(defaultFunctionRates);
    }, [defaultFunctionRates, paymentDataLoaded]);

    useEffect(() => {
        if (!paymentDataLoaded || skipPaymentPersistRef.current) return;
        saveTaxRate(taxRate);
    }, [taxRate, paymentDataLoaded]);

    useEffect(() => {
        if (!paymentDataLoaded) return;
        skipPaymentPersistRef.current = false;
    }, [paymentDataLoaded]);

    // Ładowanie danych płatności z bazy danych przy starcie
    // Nasłuchuj na wiadomości z Service Workera (odpowiedzi na propozycje i push notifications)
    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

        const handleMessage = (event) => {
            const { type, data, result, error } = event.data || {};
            
            if (type === 'PROPOSAL_ACCEPTED' || type === 'PROPOSAL_REJECTED') {
                // Odśwież dane po akceptacji/odrzuceniu propozycji
                if (result && result.success) {
                    // Odśwież dane po krótkim opóźnieniu (aby dać czas na zapis do bazy)
                    setTimeout(() => {
                        if (reloadFromGoogle) {
                            // Odśwież dane harmonogramu bez przeładowania strony
                            reloadFromGoogle();
                        } else if (changeCalendarAndReload) {
                            changeCalendarAndReload(selectedCalendar);
                        } else {
                            window.location.reload();
                        }
                    }, 1000);
                } else if (error) {
                    console.error(`[App] Błąd ${type === 'PROPOSAL_ACCEPTED' ? 'akceptacji' : 'odrzucenia'} propozycji:`, error);
                }
            } else if (type === 'CLEAR_BADGE') {
                if (typeof navigator !== 'undefined' && 'clearAppBadge' in navigator) {
                    navigator.clearAppBadge().catch(() => {});
                }
            } else if (type === 'PUSH_NOTIFICATION_RECEIVED') {
                // Licznik i lista: NotificationsPanel (jeden getNotifications).
                // Harmonogram: usePlannerData (jeden loadData). Tu tylko toast.
                if (typeof document !== 'undefined' && document.hasFocus?.()) {
                    const title = data?.title || 'Nowe powiadomienie';
                    const body = data?.body || 'Masz nowe powiadomienie';
                    setPushToast({ title, body });
                    setTimeout(() => setPushToast(null), 5000);
                }
            }
        };

        navigator.serviceWorker.addEventListener('message', handleMessage);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
        };
    }, [reloadFromGoogle, changeCalendarAndReload, selectedCalendar]);

    // Hook do zarządzania subskrypcją push (dla promptu przy pierwszym logowaniu)
    const pushSubscription = usePushSubscription(user);

    // Sprawdź czy powinien pokazać popup z prośbą o uprawnienia przy pierwszym logowaniu
    useEffect(() => {
        if (!user || !pushSubscription.isSupported || loading) return;
        
        // Sprawdź czy użytkownik nie ma włączonych powiadomień i nie odrzucił ich wcześniej
        const permission = typeof Notification !== 'undefined' ? Notification.permission : 'default';
        const shouldShowPrompt = 
            permission === 'default' && 
            !pushSubscription.isSubscribed &&
            !localStorage.getItem('notificationPromptDismissed') &&
            !showNotificationPermissionPrompt; // Nie pokazuj jeśli już jest wyświetlony
        
        if (shouldShowPrompt) {
            // Opóźnij pokazanie popupu o 2 sekundy po zalogowaniu, aby nie przeszkadzać od razu
            const timer = setTimeout(() => {
                setShowNotificationPermissionPrompt(true);
            }, 2000);
            
            return () => clearTimeout(timer);
        }
    }, [user, pushSubscription.isSupported, pushSubscription.isSubscribed, loading, showNotificationPermissionPrompt]);

    useEffect(() => {
        if (paymentDataLoaded || loading || !user) return; // Nie ładuj jeśli już załadowane, trwa ładowanie lub użytkownik nie jest zalogowany
        
        (async () => {
            try {
                const response = await fetch(`${getApiBase()}/api/loadData`);
                if (!response.ok) {
                    console.error('❌ [App] loadData response not ok:', response.status, response.statusText);
                    setPaymentDataLoaded(true); // Oznacz jako załadowane, aby nie próbować ponownie
                    return;
                }
                if (response.ok) {
                    const data = await response.json();
                    paymentSnapshotRef.current = data;
                    
                    // Zaktualizuj dane płatności z bazy (nadpisz localStorage)
                    // Używamy danych z DB jeśli istnieją, w przeciwnym razie zostawiamy z localStorage
                    if (data.paymentRates && typeof data.paymentRates === 'object') {
                        setPaymentRates(data.paymentRates);
                        try {
                            localStorage.setItem('planner:paymentRates', JSON.stringify(data.paymentRates));
                        } catch {}
                    }
                    
                    if (data.eventStaffRoles && typeof data.eventStaffRoles === 'object') {
                        setEventStaffRoles(data.eventStaffRoles);
                        try {
                            localStorage.setItem('planner:eventStaffRoles', JSON.stringify(data.eventStaffRoles));
                        } catch {}
                    }
                    
                    if (data.eventAmounts && typeof data.eventAmounts === 'object') {
                        setEventAmounts(data.eventAmounts);
                        try {
                            localStorage.setItem('planner:eventAmounts', JSON.stringify(data.eventAmounts));
                        } catch {}
                    }
                    
                    if (data.eventStaffPayments && typeof data.eventStaffPayments === 'object') {
                        setEventStaffPayments(data.eventStaffPayments);
                        try {
                            localStorage.setItem('planner:eventStaffPayments', JSON.stringify(data.eventStaffPayments));
                        } catch {}
                    }
                    
                    if (data.defaultFunctionRates && typeof data.defaultFunctionRates === 'object') {
                        setDefaultFunctionRates(data.defaultFunctionRates);
                        try {
                            localStorage.setItem('planner:defaultFunctionRates', JSON.stringify(data.defaultFunctionRates));
                        } catch {}
                    }
                    
                    if (typeof data.taxRate === 'number') {
                        setTaxRate(data.taxRate);
                        try {
                            localStorage.setItem('planner:taxRate', String(data.taxRate));
                        } catch {}
                    }
                    
                    setPaymentDataLoaded(true);
                } else {
                    // Jeśli odpowiedź nie jest OK, ale nie ma błędu - zaznacz jako załadowane (używamy localStorage)
                    setPaymentDataLoaded(true);
                }
            } catch (err) {
                setPaymentDataLoaded(true); // Oznacz jako załadowane, aby nie próbować ponownie
            }
        })();
    }, [loading, paymentDataLoaded, user]);

    // Synchronizacja istniejących danych z localStorage do bazy (jeśli nie ma ich w bazie)
    useEffect(() => {
        if (!paymentDataLoaded) return; // Czekaj aż dane będą załadowane
        
        // Sprawdź czy w bazie są jakieś dane płatności
        (async () => {
            try {
                const data = paymentSnapshotRef.current;
                if (data) {
                    const hasPaymentData = 
                        (data.paymentRates && Object.keys(data.paymentRates).length > 0) ||
                        (data.eventStaffRoles && Object.keys(data.eventStaffRoles).length > 0) ||
                        (data.eventAmounts && Object.keys(data.eventAmounts).length > 0) ||
                        (data.eventStaffPayments && Object.keys(data.eventStaffPayments).length > 0) ||
                        (data.defaultFunctionRates && Object.keys(data.defaultFunctionRates).length > 0);
                    
                    // Jeśli nie ma danych w bazie, ale są w localStorage - zsynchronizuj
                    if (!hasPaymentData) {
                        const localPaymentRates = loadPaymentRates();
                        const localEventStaffRoles = loadEventStaffRoles();
                        const localEventAmounts = loadEventAmounts();
                        const localEventStaffPayments = loadEventStaffPayments();
                        const localDefaultFunctionRates = loadDefaultFunctionRates();
                        const localTaxRate = loadTaxRate();
                        
                        const hasLocalData = 
                            (Object.keys(localPaymentRates).length > 0) ||
                            (Object.keys(localEventStaffRoles).length > 0) ||
                            (Object.keys(localEventAmounts).length > 0) ||
                            (Object.keys(localEventStaffPayments).length > 0) ||
                            (Object.keys(localDefaultFunctionRates).length > 0) ||
                            localTaxRate > 0;
                        
                        if (hasLocalData) {
                            try {
                                // Buduj obiekt z danymi - tylko niepuste wartości
                                const dataToSync = {};
                                const paymentRatesKeys = Object.keys(localPaymentRates || {});
                                const eventStaffRolesKeys = Object.keys(localEventStaffRoles || {});
                                const eventAmountsKeys = Object.keys(localEventAmounts || {});
                                const eventStaffPaymentsKeys = Object.keys(localEventStaffPayments || {});
                                const defaultFunctionRatesKeys = Object.keys(localDefaultFunctionRates || {});
                                
                                if (paymentRatesKeys.length > 0) {
                                    dataToSync.paymentRates = localPaymentRates;
                                }
                                if (eventStaffRolesKeys.length > 0) {
                                    dataToSync.eventStaffRoles = localEventStaffRoles;
                                }
                                if (eventAmountsKeys.length > 0) {
                                    dataToSync.eventAmounts = localEventAmounts;
                                }
                                if (eventStaffPaymentsKeys.length > 0) {
                                    dataToSync.eventStaffPayments = localEventStaffPayments;
                                }
                                if (defaultFunctionRatesKeys.length > 0) {
                                    dataToSync.defaultFunctionRates = localDefaultFunctionRates;
                                }
                                // taxRate zapisz zawsze jeśli jest liczbą (nawet jeśli 0)
                                if (typeof localTaxRate === 'number' && !isNaN(localTaxRate)) {
                                    dataToSync.taxRate = localTaxRate;
                                }
                                
                                // Sprawdź czy mamy coś do wysłania
                                const syncKeys = Object.keys(dataToSync);
                                if (syncKeys.length === 0) {
                                    return;
                                }
                                
                                const response = await fetch(`${getApiBase()}/api/saveData`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(dataToSync),
                                });
                                
                                if (!response.ok) {
                                    const errorText = await response.text();
                                    
                                    // Próbuj zapisać każdy typ danych osobno (jako fallback)
                                    const syncPromises = [];
                                    if (dataToSync.paymentRates) {
                                        syncPromises.push(
                                            fetch(`${getApiBase()}/api/saveData`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ paymentRates: dataToSync.paymentRates })
                                            }).catch(() => {})
                                        );
                                    }
                                    if (dataToSync.eventStaffRoles) {
                                        syncPromises.push(
                                            fetch(`${getApiBase()}/api/saveData`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ eventStaffRoles: dataToSync.eventStaffRoles })
                                            }).catch(() => {})
                                        );
                                    }
                                    if (dataToSync.eventStaffPayments) {
                                        syncPromises.push(
                                            fetch(`${getApiBase()}/api/saveData`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ eventStaffPayments: dataToSync.eventStaffPayments })
                                            }).catch(() => {})
                                        );
                                    }
                                    if (dataToSync.defaultFunctionRates) {
                                        syncPromises.push(
                                            fetch(`${getApiBase()}/api/saveData`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ defaultFunctionRates: dataToSync.defaultFunctionRates })
                                            }).catch(() => {})
                                        );
                                    }
                                    if (dataToSync.taxRate !== undefined) {
                                        syncPromises.push(
                                            fetch(`${getApiBase()}/api/saveData`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ taxRate: dataToSync.taxRate })
                                            }).catch(() => {})
                                        );
                                    }
                                    await Promise.allSettled(syncPromises);
                                } else {
                                    const result = await response.json();
                                    console.log('✅ Zsynchronizowano dane z localStorage do bazy:', result);
                                }
                            } catch (err) {
                                console.error('❌ Błąd synchronizacji:', err);
                            }
                        }
                    }
                }
                } catch (err) {
                    // Ignoruj błędy sprawdzania danych
                }
        })();
    }, [paymentDataLoaded]);

    // Auto-assign stanowisk przy zmianie wydarzeń
    // WAŻNE: Uruchamiamy TYLKO gdy dane są załadowane z bazy, aby nie nadpisać istniejących stanowisk
    useEffect(() => {
        if (eventsByDate && Object.keys(eventsByDate).length > 0 && paymentDataLoaded) {
            // Użyj aktualnych eventStaffRoles (które mogą być już z bazy) zamiast ładować z localStorage
            const currentRoles = eventStaffRoles || loadEventStaffRoles();
            const updated = autoAssignRolesFromStaff(eventsByDate, currentRoles);
            
            // Aktualizuj tylko jeśli są zmiany
            const currentStr = JSON.stringify(currentRoles || {});
            const updatedStr = JSON.stringify(updated || {});
            if (currentStr !== updatedStr) {
                // Aktualizuj eventStaffRoles (auto-assign)
                setEventStaffRoles(updated);
                // autoAssignRolesFromStaff już zapisuje do bazy przez saveEventStaffRoles jeśli są zmiany
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- celowo bez eventStaffRoles: efekt go aktualizuje, dodanie spowodowałoby pętlę
    }, [eventsByDate, paymentDataLoaded]);

    const handleUpdateRole = (date, eventName, employeeName, position) => {
        const updated = updateEventStaffRole(date, eventName, employeeName, position);
        setEventStaffRoles(updated);
        // updateEventStaffRole już zapisuje do bazy przez saveEventStaffRoles
    };

    const handleUpdateAmount = (date, eventName, amount) => {
        const updated = updateEventAmount(date, eventName, amount);
        setEventAmounts(updated);
    };

    const handleUpdateStaffPayment = (date, eventName, employeeName, baseAmount, additionalCosts) => {
        const updated = updateEventStaffPayment(date, eventName, employeeName, baseAmount, additionalCosts);
        setEventStaffPayments(updated);
    };

    // 🔹 Zamknięcie menu po kliknięciu poza nim
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 🔹 Przywracanie sesji logowania
    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        const savedType = localStorage.getItem('userType');
        if (savedUser) {
            try {
                const userData = JSON.parse(savedUser);
                setUser(userData);
                setUserType(savedType || (userData.isSuperAdmin ? 'superAdmin' : userData.isAdmin ? 'admin' : 'user'));
            } catch (e) {
                console.error('Błąd parsowania danych użytkownika:', e);
                localStorage.removeItem('user');
                localStorage.removeItem('userType');
            }
        }
    }, []);

    // 🔹 Callback po udanym logowaniu
    const handleLoginSuccess = (userData) => {
        setUser(userData);
        setUserType(userData.isSuperAdmin ? 'superAdmin' : userData.isAdmin ? 'admin' : 'user');
    };

    const handleLogout = () => {
        setUser(null);
        setUserType(null);
        localStorage.removeItem('user');
        localStorage.removeItem('userType');
    };

    // 🔹 Formularz logowania
    if (!user || !userType) {
        return <LoginForm onLoginSuccess={handleLoginSuccess} />;
    }

    const resolvedUserName =
        user?.name?.trim() ||
        user?.login?.trim() ||
        user?.id?.trim() ||
        'Nieznany użytkownik';

    const resolvedUserLogin = (() => {
        if (!user?.login) return null;
        const normalizedLogin = user.login.trim();
        return normalizedLogin && normalizedLogin !== resolvedUserName ? normalizedLogin : null;
    })();

    const userRoleLabel = (() => {
        if (userType === 'superAdmin') return 'Super admin';
        if (userType === 'admin') return 'Admin';
        if (userType === 'user') return 'Pracownik';
        return null;
    })();

    const groupByMonth = () => {
        const result = {};
        for (const date in eventsByDate) {
            const d = new Date(date);
            const key = `${d.getFullYear()}-${d.getMonth()}`;
            if (!result[key]) result[key] = [];
            result[key].push(date);
        }
        return result;
    };

    const grouped = groupByMonth();

    return (
        <Router>
            <div
                className="min-h-screen p-4 all_container
                    bg-white text-gray-900
                    dark:bg-gray-900 dark:text-gray-100"
            >
                {/* Toast przy push w PWA (gdy aplikacja jest otwarta – system często nie pokazuje powiadomienia) */}
                {pushToast && (
                    <div
                        role="alert"
                        className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] max-w-[90vw] w-full max-w-md
                            px-4 py-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600
                            bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                            flex items-start gap-3"
                    >
                        <MdNotifications className="text-blue-500 shrink-0 mt-0.5" size={24} />
                        <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm">{pushToast.title}</p>
                            {pushToast.body && <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{pushToast.body}</p>}
                        </div>
                        <button
                            type="button"
                            onClick={() => setPushToast(null)}
                            className="shrink-0 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                            aria-label="Zamknij"
                        >
                            ×
                        </button>
                    </div>
                )}
                <div className="mb-4 space-y-2">
                    {firestoreQuotaExceeded && (
                        <div className="p-3 rounded border border-red-200 bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-100 dark:border-red-900">
                            ⚠️ Przekroczono dzienny limit Firestore. Zmiany zostaną wznowione automatycznie po odnowieniu limitu.
                        </div>
                    )}
                    <div className="flex justify-end">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white text-gray-700 text-xs font-medium shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100">
                            <span className="text-base leading-none">👤</span>
                            <span className="truncate max-w-[180px]">{resolvedUserName}</span>
                            {resolvedUserLogin && (
                                <span className="text-gray-400 dark:text-gray-500 truncate max-w-[120px]">
                                    {resolvedUserLogin}
                                </span>
                            )}
                            {userRoleLabel && (
                                <span className="uppercase tracking-wide text-[10px] text-gray-500 dark:text-gray-400">
                                    {userRoleLabel}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <header
                    className="flex justify-between items-center mb-6 border-b
                     bg-white text-gray-900 border-gray-200
                     dark:bg-gray-900 dark:text-gray-100 dark:border-gray-800"
                >
                    <h1 className="text-2xl font-bold">Obsady</h1>

                    {/* Ikony: Powiadomienia i Menu */}
                    <div className="flex items-center gap-2">
                        {/* Ikona powiadomień */}
                        <div className="relative">
                            <button
                                onClick={() => setIsNotificationsPanelOpen(prev => !prev)}
                                className="btn p-2
                                         bg-white text-gray-900 border border-gray-300 rounded
                                         hover:bg-gray-50
                                         dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700
                                         relative"
                                title="Aktualności"
                            >
                                <MdNotifications size={24} />
                                {/* Licznik nieodczytanych powiadomień */}
                                {(unreadNotificationsCount > 0 || notificationQueue.queueCount > 0) && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full
                                                   w-5 h-5 flex items-center justify-center font-bold">
                                        {(unreadNotificationsCount + notificationQueue.queueCount) > 9 
                                            ? '9+' 
                                            : (unreadNotificationsCount + notificationQueue.queueCount)}
                                    </span>
                                )}
                            </button>
                            
                            {/* Panel powiadomień – zawsze zamontowany (ładuje licznik i odświeża po push) */}
                            <div
                                className={isNotificationsPanelOpen
                                    ? "fixed inset-x-2 top-16 z-[9999] md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:z-auto pointer-events-none md:pointer-events-auto"
                                    : "hidden"}
                            >
                                <div className="w-full max-w-sm md:w-96 max-h-[80vh] overflow-hidden rounded-xl shadow-2xl border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-700 pointer-events-auto">
                                    <NotificationsPanel
                                        user={user}
                                        isOpen={isNotificationsPanelOpen}
                                        onClose={() => setIsNotificationsPanelOpen(false)}
                                        onUnreadCountChange={setUnreadNotificationsCount}
                                        onRefreshSchedule={reloadFromGoogle}
                                        onLoadNotificationsReady={(fn) => { loadNotificationsRef.current = fn; }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Ikona zębatki z menu */}
                        <div className="relative" ref={menuRef}>
                        <button
                            className="btn p-2
                         bg-white text-gray-900 border border-gray-300 rounded
                         hover:bg-gray-50
                         dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
                            onClick={() => setMenuOpen((prev) => !prev)}
                            title="Menu"
                            type="button"
                        >
                            <MdSettings size={24} />
                        </button>

                        {menuOpen && (
                            <div
                                className="absolute right-0 mt-2 w-48 rounded shadow-lg z-50 border
                           bg-white text-gray-900 border-gray-200
                           dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                            >
                                <NavLink
                                    to="/"
                                    className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    📅 Harmonogram
                                </NavLink>


                                {(userType === 'admin' || userType === 'superAdmin') && (
                                    <NavLink
                                        to="/admin"
                                        className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        ⚙️ Admin
                                    </NavLink>
                                )}

                                {userType === 'superAdmin' && (
                                    <NavLink
                                        to="/payments"
                                        className="block px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        💰 $$$
                                    </NavLink>
                                )}

                                <button
                                    onClick={() => {
                                        handleLogout();
                                        setMenuOpen(false);
                                    }}
                                    className="block w-full text-left px-4 py-2
                             hover:bg-gray-50 dark:hover:bg-gray-700
                             text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                    type="button"
                                >
                                    🚪 Wyloguj
                                </button>
                            </div>
                        )}
                        </div>
                    </div>
                </header>

                <Routes>
                    <Route
                        path="/"
                        element={
                            <SchedulePage
                                /* ← loading do kręciołka w rogu wewnątrz SchedulePage */
                                loading={loading}
                                isSyncing={isSyncing}

                                eventsByDate={eventsByDate}
                                activeMonthKey={activeMonthKey}
                                setActiveMonthKey={setActiveMonthKey}
                                grouped={grouped}
                                months={months}
                                updateEventStaff={(userType === 'admin' || userType === 'superAdmin') ? updateEventStaffWithNotifications : () => { }}
                                updateEventColor={(userType === 'admin' || userType === 'superAdmin') ? updateEventColor : () => { }}
                                employees={employees}
                                updateEmployees={(userType === 'admin' || userType === 'superAdmin') ? updateEmployees : undefined}
                                isAdmin={userType === 'admin' || userType === 'superAdmin'}
                                unavailableByDate={unavailableByDate}
                                toggleUnavailable={toggleUnavailable}
                                updateNeededForEvent={updateNeededForEvent}
                                copyStaffToAllDates={copyStaffToAllDates}
                                selectedCalendar={selectedCalendar}
                                theatreCalendar={theatreCalendar}
                            />
                        }
                    />

                    <Route
                        path="/admin"
                        element={
                            (userType === 'admin' || userType === 'superAdmin') ? (
                                <AdminPage
                                    employees={employees}
                                    updateEmployees={updateEmployees}
                                    updateEmployeesAndEvents={updateEmployeesAndEvents}
                                    selectedCalendar={selectedCalendar}
                                    calendarList={calendarList}
                                    eventsByDate={eventsByDate}
                                    saveData={() => { }}
                                    changeCalendarAndReload={changeCalendarAndReload}
                                    theatreCalendar={theatreCalendar}
                                    changeTheatreCalendar={changeTheatreCalendar}
                                    paymentRates={paymentRates}
                                    setPaymentRates={setPaymentRates}
                                    defaultFunctionRates={defaultFunctionRates}
                                    setDefaultFunctionRates={setDefaultFunctionRates}
                                    taxRate={taxRate}
                                    setTaxRate={setTaxRate}
                                    userType={userType}
                                    unavailableByDate={unavailableByDate}
                                    addUnavailableRange={addUnavailableRange}
                                />
                            ) : (
                                <div className="min-h-screen p-4 flex items-center justify-center">
                                    <div className="text-center">
                                        <h2 className="text-2xl font-bold mb-4">Brak uprawnień</h2>
                                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                                            Nie masz uprawnień do dostępu do panelu administracyjnego.
                                        </p>
                                        <NavLink
                                            to="/"
                                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                            Wróć do harmonogramu
                                        </NavLink>
                                    </div>
                                </div>
                            )
                        }
                    />

                    <Route
                        path="/payments"
                        element={
                            userType === 'superAdmin' ? (
                                <PaymentsPage
                                    employees={employees}
                                    eventsByDate={eventsByDate}
                                    paymentRates={paymentRates}
                                    eventStaffRoles={eventStaffRoles}
                                    eventAmounts={eventAmounts}
                                    eventStaffPayments={eventStaffPayments}
                                    defaultFunctionRates={defaultFunctionRates}
                                    taxRate={taxRate}
                                    onUpdateRole={handleUpdateRole}
                                    onUpdateAmount={handleUpdateAmount}
                                    onUpdateStaffPayment={handleUpdateStaffPayment}
                                    selectedCalendar={selectedCalendar}
                                    isAdmin={true}
                                />
                            ) : (
                                <div className="min-h-screen p-4 flex items-center justify-center">
                                    <div className="text-center">
                                        <h2 className="text-2xl font-bold mb-4">Brak uprawnień</h2>
                                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                                            Tylko super administratorzy mają dostęp do tej sekcji.
                                        </p>
                                        <NavLink
                                            to="/"
                                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                            Wróć do harmonogramu
                                        </NavLink>
                                    </div>
                                </div>
                            )
                        }
                    />

                </Routes>

                {/* Kolejka powiadomień - wyświetla się w dolnej części ekranu */}
                <NotificationQueue
                    queue={notificationQueue.queue}
                    isVisible={notificationQueue.isQueueVisible}
                    onSend={async () => {
                        const result = await notificationQueue.sendSelectedNotifications(employees);
                        if (result.success) {
                            setIsNotificationEditorOpen(false);
                            setPushToast({ title: 'Powiadomienia', body: result.message || 'Wysłano.' });
                            setTimeout(() => setPushToast(null), 6000);
                        } else {
                            alert(`Błąd wysyłania powiadomień: ${result.message || 'Nieznany błąd'}`);
                        }
                    }}
                    onEdit={() => setIsNotificationEditorOpen(true)}
                    onReject={notificationQueue.rejectAll}
                    onClose={() => notificationQueue.setIsQueueVisible(false)}
                    selectedCount={notificationQueue.selectedCount}
                    queueCount={notificationQueue.queueCount}
                    isSending={notificationQueue.isSending}
                    unmappedCount={notificationQueue.unmappedCount}
                />

                {/* Edytor kolejki powiadomień - modal */}
                {isNotificationEditorOpen && (
                    <NotificationQueueEditor
                        queue={notificationQueue.queue}
                        onSend={async () => {
                            const result = await notificationQueue.sendSelectedNotifications(employees);
                            if (result.success) {
                                setIsNotificationEditorOpen(false);
                                setPushToast({ title: 'Powiadomienia', body: result.message || 'Wysłano.' });
                                setTimeout(() => setPushToast(null), 6000);
                            } else {
                                alert(`Błąd wysyłania powiadomień: ${result.message || 'Nieznany błąd'}`);
                            }
                        }}
                        onClose={() => setIsNotificationEditorOpen(false)}
                        onToggle={notificationQueue.toggleNotification}
                    />
                )}

                {/* Popup z prośbą o uprawnienia przy pierwszym logowaniu */}
                {showNotificationPermissionPrompt && (
                    <NotificationPermissionPrompt
                        onClose={() => {
                            setShowNotificationPermissionPrompt(false);
                            localStorage.setItem('notificationPromptDismissed', 'true');
                        }}
                        onGrant={async () => {
                            const result = await pushSubscription.subscribe();
                            if (result) {
                                localStorage.setItem('notificationPromptDismissed', 'true');
                                setShowNotificationPermissionPrompt(false);
                            }
                            return result;
                        }}
                    />
                )}
            </div>
        </Router>
    );
}
